import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence, useSpring, useMotionValue, useTransform, useReducedMotion } from "framer-motion";
import { 
  Train, Zap, Anchor, HardHat, Plane, Building, Castle, Leaf, Droplets, Route, 
  X, ArrowRight, Eye, Sparkles, Loader2, ChevronLeft, ChevronRight, RotateCcw,
  Home, Grid3X3, Navigation, Info, Star, TrendingUp, Award
} from "lucide-react";

// --- OPTIMIZED BRIDGE ICON ---
const Bridge = React.memo(({ s = 24, c = "" }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={c}>
    <path d="M3 12c0 0 3-3 9-3s9 3 9 3"/><path d="M3 18h18"/><path d="M6 12v6"/><path d="M9 12v6"/><path d="M12 12v6"/><path d="M15 12v6"/><path d="M18 12v6"/>
  </svg>
));
Bridge.displayName = 'Bridge';

// Enhanced brand colors
const theme = {
  colors: {
    primary: "#A32035",
    primaryDark: "#800020", 
    primaryLight: "#C73E54",
    accent: "#1e40af",
    success: "#059669",
    warning: "#f59e0b",
    info: "#0891b2",
    purple: "#7c3aed",
    orange: "#ea580c",
    brown: "#92400e",
    green: "#16a34a",
    blue: "#0284c7",
    rose: "#be123c",
    indigo: "#4338ca",
    gray: {
      25: "#fcfcfd",
      50: "#f9fafb",
      100: "#f2f4f7",
      200: "#eaecf0",
      300: "#d0d5dd",
      400: "#98a2b3",
      500: "#667085",
      600: "#475467",
      700: "#344054",
      800: "#1d2939",
      900: "#101828",
    }
  }
};

// --- SECTOR DATA ---
const sectorsData = [
  { 
    id: "bridges-flyovers",
    title: "Bridges & Flyovers", 
    img: "https://images.unsplash.com/photo-1736093874933-3efde26fad78?q=80&w=1374&auto=format&fit=crop", 
    description: "Engineering excellence in cable-stayed bridges, flyovers, and complex structural solutions for modern infrastructure.", 
    services: ["Cable-Stayed Bridges", "Suspension Bridges", "Flyovers & Interchanges"], 
    icon: Bridge, 
    route: "/sectors/bridges-flyovers",
    color: "#1e40af",
    stats: { projects: "150+", value: "₹5000 Cr", completion: "98%" }
  },
  { 
    id: "metro",
    title: "Metro", 
    img: "https://images.unsplash.com/photo-1612899329603-a3ce066fca6f?q=80&w=1470&auto=format&fit=crop", 
    description: "Comprehensive metro rail infrastructure including elevated viaducts, underground stations, and rapid transit systems.", 
    services: ["Elevated Viaducts", "Underground Stations", "Transit Systems"], 
    icon: Train, 
    route: "/sectors/metro",
    color: "#dc2626",
    stats: { projects: "45+", value: "₹3200 Cr", completion: "95%" }
  },
  { 
    id: "power-telecom",
    title: "Power & Telecom", 
    img: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&q=80&auto=format&fit=crop", 
    description: "Specialized engineering for thermal & hydroelectric power projects, cooling towers, and energy infrastructure solutions.", 
    services: ["Thermal Power", "Cooling Towers", "Energy Infrastructure"], 
    icon: Zap, 
    route: "/sectors/power-telecom",
    color: "#f59e0b",
    stats: { projects: "80+", value: "₹2800 Cr", completion: "92%" }
  },
  { 
    id: "offshore-coastal",
    title: "Offshore & Coastal", 
    img: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=600&q=80&auto=format&fit=crop", 
    description: "Marine infrastructure expertise in ports, harbors, jetties, breakwaters, and comprehensive coastal engineering.", 
    services: ["Ports & Harbors", "Jetties & Breakwaters", "Marine Structures"], 
    icon: Anchor, 
    route: "/sectors/offshore-coastal",
    color: "#0891b2",
    stats: { projects: "60+", value: "₹4100 Cr", completion: "94%" }
  },
  { 
    id: "construction-tech",
    title: "Construction Engineering", 
    img: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80&auto=format&fit=crop", 
    description: "Advanced construction techniques, technology transfer, and innovative engineering solutions for integrated project delivery.", 
    services: ["Advanced Construction", "Tech Transfer", "Project Integration"], 
    icon: HardHat, 
    route: "/sectors/construction-tech",
    color: "#ea580c",
    stats: { projects: "200+", value: "₹6500 Cr", completion: "96%" }
  },
  { 
    id: "airports-aviation",
    title: "Airports & Aviation", 
    img: "https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=600&q=80&auto=format&fit=crop", 
    description: "Complete airport infrastructure including terminal buildings, runways, taxiways, and aviation facility development.", 
    services: ["Terminal Buildings", "Runway Design", "Airport Master Planning"], 
    icon: Plane, 
    route: "/sectors/airports-aviation",
    color: "#7c3aed",
    stats: { projects: "25+", value: "₹1800 Cr", completion: "90%" }
  },
  { 
    id: "urban-development",
    title: "Urban Development", 
    img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80&auto=format&fit=crop", 
    description: "Comprehensive planning and development including master planning, townships, and integrated infrastructure solutions.", 
    services: ["Master Planning", "Township Development", "Industrial Infrastructure"], 
    icon: Building, 
    route: "/sectors/urban-industrial",
    color: "#059669",
    stats: { projects: "120+", value: "₹3500 Cr", completion: "93%" }
  },
  { 
    id: "heritage-buildings",
    title: "Heritage Buildings", 
    img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600&q=80&auto=format&fit=crop", 
    description: "Specialized conservation, restoration, and strengthening of heritage structures, preserving their cultural and historical significance.", 
    services: ["Heritage Conservation", "Structural Restoration", "Strengthening Schemes"], 
    icon: Castle, 
    route: "/sectors/heritage-buildings",
    color: "#92400e",
    stats: { projects: "35+", value: "₹800 Cr", completion: "97%" }
  },
  { 
    id: "environmental",
    title: "Environmental Engg.", 
    img: "https://images.unsplash.com/photo-1533077162801-86490c593afb?q=80&w=1374&auto=format&fit=crop", 
    description: "Sustainable solutions including water treatment, sewage systems, and public health infrastructure.", 
    services: ["Water Treatment", "Sewage Systems", "Sustainability"], 
    icon: Leaf, 
    route: "/sectors/environmental",
    color: "#16a34a",
    stats: { projects: "90+", value: "₹2200 Cr", completion: "91%" }
  },
  { 
    id: "water-resources",
    title: "Water Resources", 
    img: "https://images.unsplash.com/photo-1414703678227-28eeae846fd4?q=80&w=1476&auto=format&fit=crop", 
    description: "Major and minor irrigation projects, water treatment systems, and comprehensive water management solutions.", 
    services: ["Irrigation Projects", "Water Management", "Water Distribution"], 
    icon: Droplets, 
    route: "/sectors/water-resources",
    color: "#0284c7",
    stats: { projects: "180+", value: "₹4500 Cr", completion: "95%" }
  },
  { 
    id: "railways",
    title: "Railways", 
    img: "https://images.unsplash.com/photo-1647354681817-70f93bdc89a2?q=80&w=1470&auto=format&fit=crop", 
    description: "Railway infrastructure including track work, railway bridges, station buildings, and dedicated freight corridors.", 
    services: ["Track Work", "Railway Bridges", "Station Design"], 
    icon: Train, 
    route: "/sectors/railways",
    color: "#be123c",
    stats: { projects: "70+", value: "₹3800 Cr", completion: "94%" }
  },
  { 
    id: "roads-highways",
    title: "Roads & Highways", 
    img: "https://images.unsplash.com/photo-1622103358651-97d6cb0df332?q=80&w=1476&auto=format&fit=crop", 
    description: "Highway design, urban roads, expressways, and comprehensive road infrastructure for enhanced connectivity.", 
    services: ["Highway Design", "Expressways", "Urban Roads"], 
    icon: Route, 
    route: "/sectors/roads-highways",
    color: "#4338ca",
    stats: { projects: "250+", value: "₹7200 Cr", completion: "96%" }
  },
];

// --- SOPHISTICATED BACKGROUND WITH BETTER PATTERNS ---
const PremiumBackground = React.memo(() => {
  const shouldReduceMotion = useReducedMotion();
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  const parallaxX = useTransform(mouseX, [0, 1], ["-1%", "1%"]);
  const parallaxY = useTransform(mouseY, [0, 1], ["-1%", "1%"]);

  const handleMouseMove = useCallback((e) => {
    if (shouldReduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width);
    mouseY.set((e.clientY - rect.top) / rect.height);
  }, [mouseX, mouseY, shouldReduceMotion]);

  return (
    <motion.div
      className="absolute inset-0 overflow-hidden"
      onMouseMove={handleMouseMove}
      style={{
        background: `
          linear-gradient(135deg, #ffffff 0%, #fafbfc 25%, #f6f8fa 50%, #f1f3f5 75%, #eef2f5 100%),
          radial-gradient(ellipse at top left, rgba(163, 32, 53, 0.02) 0%, transparent 40%),
          radial-gradient(ellipse at bottom right, rgba(30, 64, 175, 0.015) 0%, transparent 40%)
        `,
      }}
    >
      {/* Enhanced Geometric Pattern */}
      <motion.div
        className="absolute inset-0 opacity-[0.012]"
        style={{
          backgroundImage: `
            linear-gradient(30deg, ${theme.colors.primary}30 1px, transparent 1px),
            linear-gradient(150deg, ${theme.colors.accent}25 1px, transparent 1px),
            linear-gradient(${theme.colors.primary}20 1px, transparent 1px),
            linear-gradient(90deg, ${theme.colors.primary}20 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px, 80px 80px, 40px 40px, 40px 40px',
          x: parallaxX,
          y: parallaxY,
        }}
      />

      {/* Hexagonal Pattern */}
      <motion.div
        className="absolute inset-0 opacity-[0.008]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3e%3cpath d='M25 50L37.5 68.3H62.5L75 50L62.5 31.7H37.5z' fill='none' stroke='%23A32035' stroke-width='0.5'/%3e%3c/svg%3e")`,
          backgroundSize: '60px 60px',
          x: parallaxX,
          y: parallaxY,
        }}
      />

      {/* Sophisticated Floating Elements */}
      <div className="absolute inset-0">
        {/* Elegant geometric shapes */}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`shape-${i}`}
            className="absolute opacity-[0.015]"
            style={{
              width: 100 + i * 30,
              height: 100 + i * 30,
              left: `${20 + (i * 25) % 60}%`,
              top: `${15 + (i * 30) % 50}%`,
              borderRadius: i % 2 === 0 ? '50%' : '20%',
              border: `1px solid ${i % 2 === 0 ? theme.colors.primary : theme.colors.accent}`,
              background: i % 3 === 0 ? 
                `conic-gradient(from 0deg, ${theme.colors.primary}10, transparent, ${theme.colors.primary}10)` :
                `linear-gradient(45deg, ${theme.colors.accent}08, transparent)`,
            }}
            animate={shouldReduceMotion ? {} : {
              rotate: [0, 360],
              scale: [1, 1.1, 1],
              opacity: [0.015, 0.025, 0.015],
            }}
            transition={{
              duration: 25 + i * 15,
              repeat: Infinity,
              ease: "linear",
              delay: i * 5,
            }}
          />
        ))}

        {/* Subtle gradient orbs */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`orb-${i}`}
            className="absolute rounded-full blur-3xl"
            style={{
              width: 150 + i * 50,
              height: 150 + i * 50,
              left: `${25 + i * 25}%`,
              top: `${20 + i * 30}%`,
              background: `radial-gradient(circle, ${
                i % 2 === 0 ? 'rgba(163, 32, 53, 0.015)' : 'rgba(30, 64, 175, 0.01)'
              } 0%, transparent 70%)`,
            }}
            animate={shouldReduceMotion ? {} : {
              x: [0, 30, -20, 0],
              y: [0, -25, 35, 0],
              scale: [1, 1.2, 0.9, 1],
            }}
            transition={{
              duration: 20 + i * 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 6,
            }}
          />
        ))}

        {/* Radial wave pattern */}
        <div 
          className="absolute inset-0 opacity-[0.006]"
          style={{
            background: `
              radial-gradient(circle at 30% 70%, transparent 30%, ${theme.colors.primary}20 31%, transparent 32%),
              radial-gradient(circle at 70% 30%, transparent 35%, ${theme.colors.accent}15 36%, transparent 37%),
              radial-gradient(circle at 50% 50%, transparent 40%, ${theme.colors.primary}10 41%, transparent 42%)
            `,
          }}
        />
      </div>

      {/* Sophisticated noise texture */}
      <div 
        className="absolute inset-0 opacity-[0.004] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: 'overlay',
        }}
      />
    </motion.div>
  );
});
PremiumBackground.displayName = 'PremiumBackground';

// --- ENHANCED DESKTOP SECTOR ICON ---
const EnhancedSectorIcon = React.memo(({ 
  sector, 
  index, 
  onSelect, onDeselect,
  isSelected, 
  isAnySelected, 
  focusedIndex, 
  onFocus,
  totalSectors 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  
  const angle = (360 / totalSectors) * index - 90;
  const radius = window.innerWidth < 1400 ? 300 : 340;
  const x = radius * Math.cos((angle * Math.PI) / 180);
  const y = radius * Math.sin((angle * Math.PI) / 180);

  const scale = useSpring(1, { stiffness: 280, damping: 30 });
  const rotate = useSpring(0, { stiffness: 280, damping: 100 });
  const y_spring = useSpring(0, { stiffness: 280, damping: 30 });

  const handleMouseEnter = useCallback(() => {
    if (isAnySelected || isSelected) return;
    setIsHovered(true);
    onFocus?.(index);
    if (!shouldReduceMotion) {
      scale.set(1.15);
      y_spring.set(-15);
    }
  }, [scale, rotate, y_spring, isAnySelected, isSelected, onFocus, index, shouldReduceMotion]);

  const handleMouseLeave = useCallback(() => {
    if (isAnySelected || isSelected) return;
    setIsHovered(false);
    if (!shouldReduceMotion) {
      scale.set(1);
      rotate.set(0);
      y_spring.set(0);
    }
  }, [scale, rotate, y_spring, isAnySelected, isSelected, shouldReduceMotion]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (isSelected) {
      onDeselect(); // Add deselection when clicking selected icon
      return;
    }
    onSelect(sector, x, y);
  }, [onSelect, onDeselect, sector, x, y, isSelected]);
  
  const handleKeyDown = useCallback((e) => {
    if (isAnySelected || isSelected) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  }, [handleClick, isAnySelected, isSelected]);

  const isFocused = focusedIndex === index;

  return (
    <motion.div
      key={`sector-icon-${sector.id}`}
      initial={{ scale: 0, opacity: 0, rotate: -180, y: 100 }}
      animate={{
        scale: isAnySelected && !isSelected ? 0.75 : 1,
        opacity: isSelected ? 0 : isAnySelected ? 0.25 : 1,
        filter: isAnySelected && !isSelected ? "grayscale(80%) brightness(0.7)" : "grayscale(0%)",
        rotate: 0,
        y: 0,
      }}
      exit={{ scale: 0, opacity: 0, rotate: 180, y: -100 }}
      whileTap={!isAnySelected && !isSelected ? { scale: 0.9 } : {}}
      onClick={!isAnySelected && !isSelected ? handleClick : undefined}
      onMouseEnter={!isAnySelected ? handleMouseEnter : undefined}
      onMouseLeave={!isAnySelected ? handleMouseLeave : undefined}
      onKeyDown={!isAnySelected ? handleKeyDown : undefined}
      className={`absolute group focus:outline-none ${!isAnySelected && !isSelected ? 'cursor-pointer' : 'cursor-default'}`}
      style={{ 
        top: `calc(50% + ${y}px - 80px)`, 
        left: `calc(50% + ${x}px - 80px)`,
        pointerEvents: isSelected ? 'none' : 'auto',
        scale: shouldReduceMotion ? 1 : scale,
        rotate: shouldReduceMotion ? 0 : rotate,
        y: shouldReduceMotion ? 0 : y_spring,
        zIndex: isSelected ? 0 : 10,
      }}
      tabIndex={isAnySelected ? -1 : 0}
      role="button"
      transition={{ 
        initial: { duration: 0.8, delay: index * 0.1, ease: "easeOut" },
        default: { duration: 0.5, ease: "easeOut" }
      }}
      data-sector-icon // Add this attribute
    >
      {/* Premium glow effect */}
      <motion.div 
        className="absolute inset-0 rounded-full"
        animate={{
          boxShadow: (isHovered || isFocused) && !isAnySelected && !isSelected
            ? `0 0 120px ${sector.color}30, 0 0 200px ${sector.color}15, 0 40px 80px rgba(0,0,0,0.15)`
            : '0 0 0px transparent, 0 0 0px transparent, 0 25px 50px rgba(0,0,0,0.08)',
        }}
        transition={{ duration: 0.5 }}
      />
      
      {/* Focus ring */}
      {isFocused && !isAnySelected && !isSelected && (
        <motion.div 
          className="absolute inset-0 rounded-full border-2 border-dashed"
          style={{ borderColor: `${sector.color}70` }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1.2, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: `${sector.color}40` }}
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
        </motion.div>
      )}
      
      <motion.div 
        className="relative w-[160px] h-[160px] rounded-full overflow-hidden shadow-2xl border-4 bg-white backdrop-blur-sm"
        animate={{
          borderColor: (isHovered || isFocused) && !isAnySelected && !isSelected ? `${sector.color}60` : theme.colors.gray[200],
          boxShadow: (isHovered || isFocused) && !isAnySelected && !isSelected
            ? `0 30px 60px rgba(0,0,0,0.2), 0 0 0 6px ${sector.color}15, inset 0 4px 0 rgba(255,255,255,0.9)`
            : '0 20px 40px rgba(0,0,0,0.12), 0 0 0 2px rgba(0,0,0,0.05), inset 0 4px 0 rgba(255,255,255,0.9)',
        }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <motion.img 
          src={sector.img} 
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          animate={{ 
            scale: isHovered && !isAnySelected && !isSelected ? 1.15 : 1,
            filter: isHovered && !isAnySelected && !isSelected ? "brightness(1.1) contrast(1.1)" : "brightness(1) contrast(1)"
          }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/85 to-white/90"
          animate={{ opacity: isHovered && !isAnySelected && !isSelected ? 0.75 : 0.82 }}
          transition={{ duration: 0.4 }}
        />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center p-4 text-center">
          <motion.div
            animate={{
              scale: isHovered && !isAnySelected && !isSelected ? 1.2 : 1,
              y: isHovered && !isAnySelected && !isSelected ? -8 : 0,
            }}
            transition={{ duration: 0.4 }}
            className="mb-3 flex items-center justify-center"
          >
            <div 
              className="p-2.5 rounded-xl backdrop-blur-sm border-2 shadow-lg flex items-center justify-center"
              style={{ 
                backgroundColor: isHovered && !isAnySelected && !isSelected ? `${sector.color}20` : `${sector.color}12`,
                borderColor: isHovered && !isAnySelected && !isSelected ? `${sector.color}40` : `${sector.color}25`,
                minWidth: '60px',
                minHeight: '60px',
              }}
            >
              <sector.icon 
                size={28} 
                style={{ color: isHovered && !isAnySelected && !isSelected ? sector.color : theme.colors.gray[700] }} 
              />
            </div>
          </motion.div>
          
          <motion.h3 
            className="text-xs font-bold leading-tight uppercase tracking-wider mb-2 px-1"
            style={{ color: isHovered && !isAnySelected && !isSelected ? sector.color : theme.colors.gray[800] }}
            animate={{ y: isHovered && !isAnySelected && !isSelected ? -4 : 0 }}
            transition={{ duration: 0.4 }}
          >
            {sector.title}
          </motion.h3>
          
          {/* Enhanced stats display */}
        
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: isHovered && !isAnySelected && !isSelected ? 1 : 0,
              y: isHovered && !isAnySelected && !isSelected ? 0 : 10,
            }}
            transition={{ duration: 0.3, delay: isHovered && !isAnySelected && !isSelected ? 0.15 : 0 }}
            className="flex items-center justify-center text-xs font-semibold"
            style={{ color: sector.color }}
          >
            <Sparkles size={10} className="mr-1 animate-pulse" />
            Explore
            <ArrowRight size={10} className="ml-1" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
});
EnhancedSectorIcon.displayName = 'EnhancedSectorIcon';

// --- NAVIGATION CONTROLS ---
const NavigationControls = React.memo(({ 
  selectedSector, 
  onPrevious, 
  onNext, 
  onReset, 
  onHome,
  totalSectors,
  focusedIndex 
}) => {
  if (selectedSector) return null;

  return (
    <motion.div 
      className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
      initial={{ y: 120, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 120, opacity: 0 }}
      transition={{ delay: 1, duration: 0.6, ease: "easeOut" }}
    >
      <div className="flex items-center gap-3 bg-white/95 backdrop-blur-xl rounded-2xl p-3 shadow-2xl border-2 border-gray-200">
        <motion.button
          onClick={onPrevious}
          className="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-300 border-2 border-gray-200 group shadow-sm"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
          whileTap={{ scale: 0.95 }}
          aria-label="Previous sector"
        >
          <ChevronLeft size={20} className="text-gray-600 group-hover:text-gray-800" />
        </motion.button>
        
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 border-2 border-gray-200">
          <Grid3X3 size={16} className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">
            {focusedIndex !== null ? focusedIndex + 1 : '—'} / {totalSectors}
          </span>
        </div>
        
        <motion.button
          onClick={onNext}
          className="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-300 border-2 border-gray-200 group shadow-sm"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
          whileTap={{ scale: 0.95 }}
          aria-label="Next sector"
        >
          <ChevronRight size={20} className="text-gray-600 group-hover:text-gray-800" />
        </motion.button>
        
        <div className="w-px h-8 bg-gray-300" />
        
        <motion.button
          onClick={onReset}
          className="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-300 border-2 border-gray-200 group shadow-sm"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
          whileTap={{ scale: 0.95 }}
          aria-label="Reset view"
        >
          <RotateCcw size={20} className="text-gray-600 group-hover:text-gray-800" />
        </motion.button>
        
        <motion.button
          onClick={onHome}
          className="p-3 rounded-xl transition-all duration-300 border-2 group shadow-sm"
          style={{ 
            backgroundColor: `${theme.colors.primary}10`,
            borderColor: `${theme.colors.primary}25`,
          }}
          whileHover={{ 
            scale: 1.05, 
            backgroundColor: `${theme.colors.primary}15`,
            boxShadow: `0 10px 30px ${theme.colors.primary}20` 
          }}
          whileTap={{ scale: 0.95 }}
          aria-label="Home"
        >
          <Home size={20} style={{ color: theme.colors.primary }} />
        </motion.button>
      </div>
    </motion.div>
  );
});
NavigationControls.displayName = 'NavigationControls';

// --- ENHANCED DETAIL CARD ---
const EnhancedDetailCard = React.memo(({ selectedInfo, onDeselect }) => {
  if (!selectedInfo) return null;

  const { sector } = selectedInfo;

  return (
    <motion.div
      data-detail-card // Add this attribute
      layoutId={`sector-${sector.id}`}
      className="z-50 w-[500px] h-[500px] bg-white text-gray-800 rounded-full flex items-center justify-center shadow-2xl overflow-hidden pointer-events-auto relative border-4"
      style={{
        borderColor: `${sector.color}40`,
        boxShadow: `0 60px 120px rgba(0,0,0,0.2), 0 0 0 4px ${sector.color}15, inset 0 6px 0 rgba(255,255,255,0.9)`,
        position: 'absolute',
        left: '35%',
        top: '22%',
        transform: 'translate(-50%, -50%)',
      }}
      initial={{ scale: 0.5, opacity: 0,  }}
      animate={{ 
        scale: 1, 
        opacity: 1, 
        rotate: 0,
      }}
      exit={{ 
        scale: 0.5, 
        opacity: 0, 
        rotate: 170,
      }}
      transition={{ type: "spring", stiffness: 240, damping: 25 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <img 
          src={sector.img} 
          alt=""
          className="w-full h-full object-cover opacity-5 scale-110"
        />
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            background: `
              radial-gradient(circle at 30% 70%, ${sector.color}25 0%, transparent 50%), 
              radial-gradient(circle at 70% 30%, ${sector.color}20 0%, transparent 50%),
              conic-gradient(from 0deg at 50% 50%, ${sector.color}08, transparent, ${sector.color}08)
            `,
          }}
        />
      </div>
      
      <motion.div 
        className="relative z-10 w-full text-center px-12 py-8 flex flex-col items-center justify-center h-full"
        initial="hidden"
        animate="visible"
        variants={{
          visible: { 
            opacity: 1,
            transition: { staggerChildren: 0.15, delayChildren: 0.4 }
          }
        }}
      >
        {/* Enhanced icon */}
        <motion.div 
          variants={{ hidden: { y: -40, opacity: 0, scale: 0.5 }, visible: { y: 0, opacity: 1, scale: 1 } }} 
          className="mb-4 flex items-center justify-center"
        >
          <div 
            className="p-4 rounded-3xl border-4 shadow-xl flex items-center justify-center"
            style={{ 
              backgroundColor: `${sector.color}12`, 
              borderColor: `${sector.color}30`,
              width: '80px',
              height: '80px',
            }}
          >
            <sector.icon size={40} style={{ color: sector.color }} />
          </div>
        </motion.div>
        
        {/* Title */}
        <motion.h2 
          className="text-2xl font-bold mb-3 uppercase tracking-wide text-center" 
          style={{ color: sector.color }} 
          variants={{ hidden: { y: -30, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
        >
          {sector.title}
        </motion.h2>
        
        {/* Enhanced stats */}
     
        
        {/* Description */}
        <motion.p 
          className="text-sm leading-relaxed mb-4 text-gray-700 max-w-sm mx-auto text-center" 
          variants={{ hidden: { y: 30, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
        >
          {sector.description}
        </motion.p>
        
       
        
        {/* CTA Button */}
        <motion.button
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = sector.route;
          }}
          className="text-white px-6 py-3 rounded-3xl font-bold text-sm shadow-xl flex items-center gap-2 mx-auto transition-all duration-300 group border-4"
          style={{ 
            backgroundColor: sector.color,
            borderColor: `${sector.color}60`,
            boxShadow: `0 20px 50px ${sector.color}40`,
          }}
          variants={{ hidden: { y: 35, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
          whileHover={{ 
            scale: 1.05, 
            boxShadow: `0 25px 60px ${sector.color}60`,
          }}
          whileTap={{ scale: 0.98 }}
        >
          <Navigation size={16} />
          Explore {sector.title}
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </motion.button>
      </motion.div>
      
      {/* Enhanced close button */}
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          onDeselect();
        }}
        className="absolute top-6 right-6 p-3 rounded-full bg-gray-50/95 hover:bg-gray-100/95 transition-all duration-200 shadow-xl border-4 border-gray-200 backdrop-blur-sm flex items-center justify-center"
        style={{ zIndex: 51, width: '48px', height: '48px' }}
        whileHover={{ scale: 1.1, rotate: 90 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Close details"
      >
        <X size={20} className="text-gray-600" />
      </motion.button>
    </motion.div>
  );
});
EnhancedDetailCard.displayName = 'EnhancedDetailCard';

// --- ENHANCED CENTRAL CONTENT ---
const EnhancedCentralContent = React.memo(() => (
  <motion.div 
    className="text-center relative pointer-events-none central-content" // Add central-content class
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -50 }}
    transition={{ duration: 1, ease: "easeOut" }}
  >
    <motion.div
      className="absolute rounded-full bg-white/90 backdrop-blur-xl border-3 border-gray-200 shadow-2xl flex items-center justify-center"
      style={{
        position: 'absolute',
        width: '500px', 
        height: '500px',
        left: '-4.5%',
        top: '-25%',
        transform: 'translate(-50%, -50%)',
        boxShadow: `
          0 40px 80px rgba(0,0,0,0.1), 
          inset 0 6px 0 rgba(255,255,255,0.9), 
          0 0 0 4px ${theme.colors.primary}10,
          0 0 300px ${theme.colors.primary}08
        `,
      }}
      initial={{ scale: 0.7, opacity: 0 }}
      animate={{ 
        scale: 1, 
        opacity: 1,
      }}
      transition={{ duration: 1.2, ease: "easeOut" }}
    />
    
    <div className="relative z-10 px-12 py-16 flex flex-col items-center justify-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
        className="text-center"
      >
        <h1 
          className="text-4xl md:text-5xl font-bold mb-6 uppercase tracking-wider"
          style={{ 
            color: theme.colors.primary,
            textShadow: '4px 4px 8px rgba(163,32,53,0.15)',
          }}
        >
          Our Sectors
        </h1>
      </motion.div>
      
      <motion.div 
        className="w-32 h-1.5 mx-auto mb-6 rounded-full shadow-lg"
        style={{ backgroundColor: theme.colors.primary }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
      />
      
      <motion.p 
        className="text-base md:text-lg font-light text-gray-600 max-w-lg mx-auto leading-relaxed mb-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
      >
        Explore our expertise across India's infrastructure
      </motion.p>
      
      <motion.div
        className="flex items-center justify-center gap-3 text-sm text-gray-500 mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
      >
        <Sparkles size={18} className="animate-pulse" style={{ color: theme.colors.primary }} />
        <span>Click any sector to discover more</span>
        <Info size={14} className="text-gray-400" />
      </motion.div>
      
      <motion.div
        className="text-xs text-gray-400 font-medium text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
      >
       Infrastructure Solutions
      </motion.div>
    </div>
  </motion.div>
));
EnhancedCentralContent.displayName = 'EnhancedCentralContent';

// --- ENHANCED MOBILE CARD WITH DIRECT NAVIGATION ---
const EnhancedMobileCard = React.memo(({ sector, index }) => (

  
  
  <motion.div
    className="group bg-white rounded-3xl overflow-hidden shadow-xl border-2 border-gray-200 cursor-pointer relative hover:shadow-2xl transition-all duration-500"
    initial={{ opacity: 0, y: 40, scale: 0.9 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ delay: index * 0.1, duration: 0.7, ease: "easeOut" }}
    onClick={() => {
      window.location.href = sector.route;
    }}
    whileHover={{ 
      y: -15, 
      borderColor: `${sector.color}40`,
      boxShadow: `0 35px 70px rgba(0,0,0,0.15), 0 0 0 4px ${sector.color}15`, 
    }}
    whileTap={{ scale: 0.97 }}
  >
    {/* Background Image */}
    <div className="relative h-48 overflow-hidden">
      <motion.img 
        src={sector.img} 
        alt=""
        className="w-full h-full object-cover"
        whileHover={{ scale: 1.1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />
      <div 
        className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"
      />
      <div 
        className="absolute inset-0 bg-gradient-to-br opacity-20"
        style={{ background: `linear-gradient(135deg, ${sector.color}80, transparent)` }} 
      />
      
      {/* Sector Icon Overlay */}
      <div className="absolute top-4 right-4">
        <div 
          className="p-3 rounded-2xl backdrop-blur-sm border-2 shadow-lg flex items-center justify-center"
          style={{ 
            backgroundColor: `${sector.color}15`,
            borderColor: `${sector.color}30`,
            width: '56px',
            height: '56px',
          }}
        >
          <sector.icon size={24} style={{ color: sector.color }} />
        </div>
      </div>
      
      {/* Stats Overlay */}
      <div className="absolute bottom-4 left-4 flex gap-2">
        <span 
          className="px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm border flex items-center justify-center"
          style={{ 
            backgroundColor: `${sector.color}90`,
            color: 'white',
            borderColor: `${sector.color}`,
            minWidth: '50px',
          }}
        >
          {sector.stats.projects}
        </span>
        <span 
          className="px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm border flex items-center justify-center"
          style={{ 
            backgroundColor: `${sector.color}90`,
            color: 'white',
            borderColor: `${sector.color}`,
            minWidth: '50px',
          }}
        >
        </span>
      </div>
    </div>
    
    {/* Content Section */}
    <div className="p-6">
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold mb-2 uppercase tracking-wider text-gray-800 group-hover:text-gray-900">
          {sector.title}
        </h3>
        
        <div 
          className="w-16 h-1 mx-auto rounded-full"
          style={{ backgroundColor: sector.color }}
        />
      </div>
      
      <p className="text-sm text-gray-600 leading-relaxed text-center mb-6 line-clamp-3 group-hover:text-gray-700">
        {sector.description}
      </p>
      
      {/* Services Pills */}
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {sector.services.slice(0, 2).map((service, i) => (
          <span 
            key={i}
            className="text-xs px-3 py-1 rounded-full border font-medium text-center"
            style={{ 
              backgroundColor: `${sector.color}08`,
              color: sector.color,
              borderColor: `${sector.color}25`,
            }}
          >
            {service}
          </span>
        ))}
        {sector.services.length > 2 && (
          <span 
            className="text-xs px-3 py-1 rounded-full border font-medium text-center"
            style={{ 
              backgroundColor: `${sector.color}08`,
              color: sector.color,
              borderColor: `${sector.color}25`,
            }}
          >
            +{sector.services.length - 2} more
          </span>
        )}
      </div>
      
      {/* CTA */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 text-base font-semibold group-hover:gap-4 transition-all duration-300" style={{ color: sector.color }}>
          <Sparkles size={18} />
          <span>Explore Now</span>
          <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
        </div>
      </div>
    </div>
  </motion.div>
));
EnhancedMobileCard.displayName = 'EnhancedMobileCard';

// --- MAIN COMPONENT ---
const PremiumSectorsComponent = () => {
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const containerRef = useRef(null);

  const sectors = useMemo(() => sectorsData, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Enhanced keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedInfo) {
        switch (e.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault();
            setFocusedIndex(prev => 
              prev === null ? 0 : (prev - 1 + sectors.length) % sectors.length
            );
            break;
          case 'ArrowRight':
          case 'ArrowDown':
            e.preventDefault();
            setFocusedIndex(prev => 
              prev === null ? 0 : (prev + 1) % sectors.length
            );
            break;
          case 'Enter':
          case ' ':
            if (focusedIndex !== null) {
              e.preventDefault();
              handleSectorSelect(sectors[focusedIndex]);
            }
            break;
          case 'Home':
            e.preventDefault();
            setFocusedIndex(0);
            break;
          case 'End':
            e.preventDefault();
            setFocusedIndex(sectors.length - 1);
            break;
        }
      }
      
      if (e.key === 'Escape') {
        if (selectedInfo) {
          handleDeselect();
        } else {
          setFocusedIndex(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedInfo, focusedIndex, sectors]);

  const handleSectorSelect = useCallback((sector, x = 0, y = 0) => {
    setSelectedInfo({ sector, x, y });
  }, []);

  const handleDeselect = useCallback(() => {
    setSelectedInfo(null);
  }, []);

  const handlePrevious = useCallback(() => {
    setFocusedIndex(prev => 
      prev === null ? sectors.length - 1 : (prev - 1 + sectors.length) % sectors.length
    );
  }, [sectors.length]);

  const handleNext = useCallback(() => {
    setFocusedIndex(prev => 
      prev === null ? 0 : (prev + 1) % sectors.length
    );
  }, [sectors.length]);

  const handleReset = useCallback(() => {
    setFocusedIndex(null);
    setSelectedInfo(null);
  }, []);

  const handleHome = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Add handleContainerClick to handle background clicks
  const handleContainerClick = useCallback((e) => {
    // Get the clicked element
    const target = e.target;

    // Check if click is on the central content
    const centralContent = document.querySelector('.central-content');
    if (centralContent?.contains(target)) {
      return; // Do nothing if clicking central content
    }

    // Check if click is on a sector icon or detail card
    const isOnSector = target.closest('[data-sector-icon]');
    const isOnDetail = target.closest('[data-detail-card]');
    
    if (!isOnSector && !isOnDetail && selectedInfo) {
      handleDeselect();
    }
  }, [selectedInfo, handleDeselect]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="mb-8 flex justify-center"
          >
            <Loader2 className="w-20 h-20" style={{ color: theme.colors.primary }} />
          </motion.div>
          <motion.h2 
            className="text-2xl font-bold text-gray-800 mb-4"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Loading Infrastructure Sectors
          </motion.h2>
          <p className="text-lg text-gray-600">Preparing your premium exploration experience...</p>
        </motion.div>
      </div>
    );
  }

  const selected = selectedInfo?.sector;

  return (
    <div 
      ref={containerRef}
      onClick={handleContainerClick} // Add click handler
      className="min-h-screen relative overflow-hidden flex flex-col font-sans bg-white"
    >
      <PremiumBackground />
      
      {/* Skip to content link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-red-600 text-white px-4 py-2 rounded-lg z-[100]"
      >
        Skip to main content
      </a>
      
      {/* Desktop View */}
      <div id="main-content" className="hidden lg:flex flex-grow items-center justify-center relative">
        <div className="absolute inset-0 z-10">
         {sectors.map((sector, index) => (
  <EnhancedSectorIcon 
    key={sector.id}
    sector={sector} 
    index={index} 
    onSelect={handleSectorSelect}
    onDeselect={handleDeselect}  // Add this line
    isSelected={selected?.id === sector.id}
    isAnySelected={!!selected}
    focusedIndex={focusedIndex}
    onFocus={setFocusedIndex}
    totalSectors={sectors.length}
  />
))}
        </div>
        
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <AnimatePresence mode="wait">
            {selectedInfo ? (
              <div className="pointer-events-auto" key={selectedInfo.sector.id}>
                <EnhancedDetailCard 
                  selectedInfo={selectedInfo}
                  onDeselect={handleDeselect} 
                />
              </div>
            ) : (
              <div className="pointer-events-none" key="default-content">
                <EnhancedCentralContent />
              </div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Navigation Controls */}
       
      </div>
      
      {/* Enhanced Mobile View */}
      <div className="lg:hidden p-6 pt-16 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl font-bold mb-6 uppercase tracking-wider" style={{ color: theme.colors.primary }}>
            Our Sectors
          </h1>
          <div className="w-32 h-1.5 mx-auto rounded-full mb-4" style={{ backgroundColor: theme.colors.primary }} />
          <p className="text-lg text-gray-600 mb-4">Tap any sector to explore our expertise</p>
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Star size={16} />
            <span className="text-sm">Premium Infrastructure Solutions</span>
            <Star size={16} />
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {sectors.map((sector, index) => (
            <EnhancedMobileCard
              key={sector.id}
              sector={sector}
              index={index}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PremiumSectorsComponent;